import React from "react"
import { PDFCheckBox, PDFDocument, PDFForm, PDFTextField, PDFRadioGroup } from "pdf-lib";
import { ProgramContainer } from "../entities/formTemplateDataType"
import ReactDOMServer from "react-dom/server";
import html2pdf from 'html2pdf.js';
import { provinceMap } from "../entities/Enums";
import URLUtils, { getBxpAssetCandidates } from "../utilities/URLUtils";
import { isBxpForm } from "../utilities/ElementUtils";

const appendTimestampFooter = (pdf: any) => {
    const width = pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.getHeight();
    const totalPages = pdf.internal.getNumberOfPages();
    const timestamp = new Date().toLocaleString();
    const copyright = "© King's Printer for Ontario, 26";

    for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.text(copyright, 10, height - 10, { align: 'left' });
        pdf.text(timestamp, width - 10, height - 10, { align: 'right' });
    }
};

export const generateAndDownloadPDF = async (programContainer: ProgramContainer, lang = 'en') => {
    const pdfBytes = await generatePDF(programContainer, lang);
    if (pdfBytes) {
        downloadFile(pdfBytes, 'application/pdf');
    } else {
        // Fallback: capture the current page content as HTML-to-PDF
        const contentElement = document.querySelector('.ontario-form-container')
            || document.querySelector('#main-content')
            || document.body;
        if (contentElement) {
            const opt = {
                margin: [20, 10, 20, 10],
                filename: `${programContainer.CategoryId || 'document'}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: {
                    scale: 2, useCORS: true, onclone: (doc: Document) => {
                        doc.querySelectorAll('button').forEach(el => el.remove());
                        doc.querySelectorAll('.step-indicator-step').forEach(el => el.remove());
                        doc.querySelectorAll('.ontario-button').forEach(el => el.remove());
                    }
                }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: { mode: ['css', 'legacy'] },
            };
            const pdf = await html2pdf().set(opt).from(contentElement).toPdf().get('pdf');
            appendTimestampFooter(pdf);
            pdf.save(opt.filename);
        }
    }
}

export const generatePDF = async (programContainer: ProgramContainer, lang = 'en') => {

    // const index = programContainer.pdfTemplate.indexOf(',');
    // const pdfData = programContainer.pdfTemplate.substring(index+1)
    // const pdfBytes = atob(pdfData);
    const categoryId = programContainer.CategoryId;
    let pdfDoc = null;
    if (isBxpForm(categoryId)) {
        const fileName = `${categoryId.toUpperCase()}${lang == 'en' ? 'E' : 'F'}.pdf`;
        const pdfCandidates = getBxpAssetCandidates(`pdf-templates/${lang}/${fileName}`);
        let response: Response | null = null;
        try {
            for (const pdfPath of pdfCandidates) {
                const candidateResponse = await fetch(pdfPath);
                if (!candidateResponse.ok) {
                    continue;
                }

                const contentType = candidateResponse.headers.get('content-type');
                if (!contentType?.includes('pdf')) {
                    continue;
                }

                response = candidateResponse;
                break;
            }

            if (!response) {
                console.error(`PDF template not found for ${fileName}`);
                return null;
            }
        } catch (error) {
            console.error(`Error loading PDF template: ${error}`);
            return null;
        }
        const pdfData = await response.arrayBuffer();
        pdfDoc = await PDFDocument.load(pdfData);
    } else {
        if ((lang == 'en' && !programContainer.pdfTemplate) || (lang == 'fr' && !programContainer.pdfTemplateFRA)) return null;
        const pdfData = lang == 'en' ? programContainer.pdfTemplate : programContainer.pdfTemplateFRA;
        pdfDoc = await PDFDocument.load(pdfData);
    }

    const form = pdfDoc.getForm();
    if (!form) return null;

    // List form fields
    // const fields = form.getFields();
    // fields.forEach(field => {
    //      console.log(`Field name: ${field.getName()}, Type: ${field.constructor.name}`);
    // });

    populatePDFWithElementValues(programContainer, form, lang);
    makePDFReadOnly(form);

    // following code to verify the values of each field afer update.
    // const fields = form.getFields() ?? [];
    // fields.forEach(field => {
    //     const type = field.constructor.name;
    //     const name = field.getName();
    //     let val = null;
    //     switch(type) {
    //         case 'PDFTextField':
    //             val = (field as PDFTextField).getText();
    //             break;
    //         case 'PDFCheckBox':
    //             val = (field as PDFCheckBox).isChecked();
    //             break;
    //     };

    //     console.log(`name: ${name}, type: ${type}, value: ${val}`);
    // });


    const pdfBytes = await form.doc.save();
    return pdfBytes
}

export const uint8ToBase64 = (uint8array: Uint8Array): string => {
    let binary = '';
    const len = uint8array.byteLength;

    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(uint8array[i]);
    }

    return btoa(binary);
}

export const generateBase64PdfTemplate = async (programContainer: ProgramContainer, lang = 'en') => {
    const pdfBytes = await generatePDF(programContainer, lang);

    if (!pdfBytes) return null;

    const base64String = uint8ToBase64(pdfBytes);
    return base64String;
}

export const downloadFile = (pdfData, mimeType) => {
    if (pdfData) {
        const blob = new Blob([pdfData], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'downloaded.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
    } else {
        alert('No PDF loaded. Please select a PDF file first.');
    }
};

const convertDateFormat = (value: unknown) => {
    // Preserve plain objects as-is
    if (value !== null && typeof value === 'object' && (value as Object).constructor === Object) {
        return value;
    }

    if (typeof value !== 'string') return value;

    const parts = value.split('/').map(p => p.trim());
    if (parts.length !== 3) return value;

    const [month, day, year] = parts;
    if (!day || !month || !year) return value;

    // Zero-pad day/month if single digit and numeric
    const num1to2 = /^\d{1,2}$/;
    const dd = num1to2.test(day) ? day.padStart(2, '0') : day;
    const mm = num1to2.test(month) ? month.padStart(2, '0') : month;

    // Convert dd/mm/yyyy -> mm/dd/yyyy....
    return `${year}/${mm}/${dd}`;
}

const populatePDFWithElementValues = (programContainer: ProgramContainer, form: PDFForm, lang = 'en') => {
    programContainer.ListOfFormSections.forEach((section, i) => {
        if (section.Visible === false) return;
        section.ListOfFormSubSections.forEach(subSec => {
            if (subSec.Visible === false) return;
            subSec.ListOfFormSectionElements.forEach(element => {
                if (element.Visible === false && element.hideInSummary !== true) return; //here hideInSummary is for the field that is invisible but still want to show in PDF 
                //const fieldName = lang === 'en' ? element.ENUTitle : element.FRATitle;
                const fieldName = element.LoadBindName;
                if (fieldName && element.Value) {
                    let ft = getFieldAndType(form, fieldName);
                    let field = ft.field;
                    let type = ft.type;

                    switch (element.Datatype) {
                        case 'ODSDate':
                        case 'Text':
                        case 'MultiText':
                        case 'PostalCode':
                        case 'PhoneNumber':
                        case 'Number':
                        case 'Currency':
                        case 'BusinessNumber':
                        case 'Date':
                        case 'Email':
                            if (type === 'PDFTextField' || type === 't') {
                                if (typeof element.Value === 'string' || typeof element.Value === 'number') {
                                    (field as PDFTextField).setText(String(element.Value));
                                } else {
                                    (field as PDFTextField).setText('');
                                }
                            }
                            if (element.Datatype === 'ODSDate') {
                                (field as PDFTextField).setText(String(convertDateFormat(element.Value)));
                            }
                            break;
                        case 'Checkbox':
                            if (type === 'PDFCheckBox') {
                                element.Value?.toLowerCase() === 'y' ? (field as PDFCheckBox).check() : (field as PDFCheckBox).uncheck();
                            }
                            break;
                        case 'CheckboxWithTextbox':
                            if (type === 'PDFTextField') {
                                (field as PDFTextField).setText(element.Value);
                                field.enableReadOnly();
                                const checkbox = form.getField(fieldName + '_checkbox');
                                const checkType = checkbox.constructor.name;
                                if (checkType === 'PDFCheckBox') {
                                    element.Checked ? (checkbox as PDFCheckBox).check() : (checkbox as PDFCheckBox).uncheck();
                                }
                            }
                            break;
                        case 'Address':
                            if (programContainer.SubmitAsXML) {
                                const parent = getAddressParent(fieldName);
                                const addressValues = parseAddress(element.Value);

                                Object.keys(addressValues).forEach(key => {
                                    const fullKey = parent + '.' + key;
                                    ft = getFieldAndType(form, fullKey);
                                    field = ft.field;
                                    type = ft.type;
                                    if (field) {
                                        if (type === 'PDFTextField' || type === 't') {
                                            (field as PDFTextField).setText(addressValues[key]);
                                        }
                                    }
                                });
                            } else {
                                Object.keys(element.Address).forEach(key => {
                                    //const fieldKey = `${key}__${i}`
                                    const fieldKey = `${element.LoadBindName}.${key}`
                                    ft = getFieldAndType(form, fieldKey);
                                    field = ft.field;
                                    type = ft.type;
                                    if (field) {
                                        if (type === 'PDFTextField' || type === 't') {
                                            (field as PDFTextField).setText(element.Address[key]);
                                        }
                                    }
                                });
                            }
                            break;
                        case 'RadioButton':
                            console.log('radio button: ', element.Value);
                            if (type === 'PDFRadioGroup') {
                                const radioGroup = field as PDFRadioGroup;
                                const options = radioGroup.getOptions();
                                console.log(options);
                                if (options.includes(element.Value)) {
                                    radioGroup.select(element.Value);
                                } else {
                                    console.debug(`${fieldName}.${element.Value} does not exist in the PDF template`)
                                }
                            }
                            break;
                        case 'File':
                            console.log('file type');
                            const isBXP = isBxpForm(programContainer.CategoryId)
                            const fileValues = parseFiles(element.Value);
                            if (isBXP) {
                                fileValues.fileSizes = fileValues.fileSizes
                                    .split('\n')
                                    .map(line => line.trim().replace(/\s*MB$/i, ''))
                                    .join('\n');
                            }
                            Object.keys(fileValues).forEach(key => {
                                let fullKey = fieldName + '.' + key;

                                ft = getFieldAndType(form, fullKey);
                                field = ft.field;
                                type = ft.type;
                                if (field) {
                                    if (type === 'PDFTextField' || type === 't') {
                                        let newText = fileValues[key];
                                        (field as PDFTextField).enableMultiline();
                                        (field as PDFTextField).setText(newText);
                                    }
                                }
                            });
                            break;
                    }

                    if (field) {
                        field.enableReadOnly();
                    }
                }
            });
        });
    });
    return form;
}

const getFieldAndType = (form: PDFForm, fieldName: string) => {
    let field = null;
    let type = null;
    console.log(fieldName);
    try {
        field = form.getField(fieldName);
        // type = field.constructor.name;
        if (field) {
            if ('setText' in field && typeof (field as any).setText === 'function') {
                type = 'PDFTextField';
            } else if ('select' in field && typeof (field as any).select === 'function') {
                type = 'PDFRadioGroup';
            } else if (
                ('check' in field && typeof (field as any).check === 'function') &&
                ('uncheck' in field && typeof (field as any).uncheck === 'function')
            ) {
                type = 'PDFCheckBox';
            }
        } else {
            console.debug(`field "${fieldName}" does not exist in the PDF file.`);
        }
        // type = field.constructor.name;
        if (field) {
            if ('setText' in field && typeof (field as any).setText === 'function') {
                type = 'PDFTextField';
            } else if ('select' in field && typeof (field as any).select === 'function') {
                type = 'PDFRadioGroup';
            } else if (
                ('check' in field && typeof (field as any).check === 'function') &&
                ('uncheck' in field && typeof (field as any).uncheck === 'function')
            ) {
                type = 'PDFCheckBox';
            }
        } else {
            console.debug(`field "${fieldName}" does not exist in the PDF file.`);
        }
    } catch (err) {
        console.debug(`error retreiving field ${fieldName}:`, err);
    }
    console.log(field, type);
    // console.log(Object.getPrototypeOf(field));
    return { field, type };
}

const makePDFReadOnly = (form: PDFForm): PDFForm => {
    const fields = form.getFields() ?? [];
    fields.forEach(field => {
        if (!field.isReadOnly()) {
            field.enableReadOnly();
        }
    });
    return form;
}


function getAddressParent(fieldName) {

    if (fieldName.endsWith('.address')) {
        return fieldName.replace(/\.address$/, '');
    }

    return '';
}


const parseAddress = (address) => {
    // Assuming address is formatted: street, city, province, postal code
    const parts = address.split(',').map(part => part.trim());

    // Not enough parts
    if (parts.length < 3) {
        console.log('not enough parts');
        return {
            street1: address,
            street2: '',
            city: '',
            province: '',
            postalCode: '',
            country: ''
        };
    }

    // Get city, province, postal code
    const city = parts[parts.length - 3];
    let province = parts[parts.length - 2];
    const provinceName = provinceMap[province.toUpperCase()];
    const postalCode = parts[parts.length - 1];

    const streetRaw = parts.slice(0, parts.length - 3).join(', ');

    // Check for unit type
    const unitPattern = /^(APT|UNIT|SUITE|#)\s*[\w-]+/i;
    let street1 = streetRaw;
    let street2 = '';

    const unitMatch = streetRaw.match(unitPattern);
    if (unitMatch) {
        const dashSplit = streetRaw.split(/\s*-\s*/);
        if (dashSplit.length === 2) {
            street2 = dashSplit[0].trim();
            street1 = dashSplit[1].trim();
        } else {
            street2 = unitMatch[0].trim();
            street1 = streetRaw.replace(unitMatch[0], '').trim();
        }
    }
    console.log(street1);
    console.log(street2);
    console.log(city);
    console.log(province);
    console.log(postalCode);

    return {
        street1,
        street2,
        city,
        province: provinceName,
        postalCode,
        country: 'Canada'
    };
}

export const generatePdfBase64 = async (htmlStr: any) => {
    const pdfJSX = () => (
        <div style={{ display: "block", width: "100%", margin: "0 auto", paddingTop: "0" }} id="pdf-content">

            <style>
                {`
                    .pdf-two-column,
                    .pdf-row-column,
                    .pdf-two-column > div,
                    .pdf-row-column > div {
                        break-inside: avoid-page !important;
                        page-break-inside: avoid !important;
                        -webkit-column-break-inside: avoid !important;
                        -moz-column-break-inside: avoid !important;
                        -webkit-region-break-inside: avoid !important;
                        page-break-before: auto;
                        page-break-after: auto;
                    }

                    .pdf-row-column {
                        display: table;
                        table-layout: fixed;
                        width: 100%;
                        box-sizing: border-box;
                        margin-bottom: 0;
                        line-height: 1.4;
                        border-collapse: collapse;
                    }

                    .pdf-row-column>div {
                        display: table-cell;
                        width: 50%;
                        padding: 0.75rem 1rem;
                        vertical-align: top;
                        box-sizing: border-box;
                        word-wrap: break-word;
                        overflow-wrap: break-word;
                        line-height: 1.4;
                        border-bottom: #d5d5d5 solid .18rem;
                    }

                    .pdf-two-column {
                        display: table;
                        table-layout: fixed;
                        width: 100%;
                        box-sizing: border-box;
                        margin-bottom: 0;
                        line-height: 1.4;
                        border-collapse: collapse;
                    }

                    .pdf-two-column > div {
                        display: table-cell;
                        width: 50%;
                        padding: 0.75rem 1rem;
                        box-sizing: border-box;
                        min-height: 3rem;
                        word-wrap: break-word;
                        overflow-wrap: break-word;
                        line-height: 1.4;
                        vertical-align: top;
                        border-bottom: #d5d5d5 solid .18rem;
                    }

                    .pdf-two-column > div:first-child {
                        background: #F2F2F2;
                        font-weight: 600;
                        border-right: #d5d5d5 solid .18rem;
                    }

                    .pdf-two-column > div:nth-child(2) {
                        text-align: left;
                    }

                    /* Hide hero/oer headers in generated PDFs */
                    .oer-wrapper,
                    .hero-shell,
                    .hero-wrapper {
                        display: none !important;
                    }
                `}
            </style>
            {htmlStr}
        </div>
    );
    const printElement = ReactDOMServer.renderToString(pdfJSX())
        .replace(/class="([^"]*\bpdf-two-column\b[^"]*)"/g, 'class="pdf-row $1" style="page-break-inside: avoid; break-inside: avoid-page; display: table; table-layout: fixed; width: 100%;"')
        .replace(/class="([^"]*\bpdf-row-column\b[^"]*)"/g, 'class="pdf-row $1" style="page-break-inside: avoid; break-inside: avoid-page; display: table; table-layout: fixed; width: 100%;"');

    const options = {
        margin: [20, 10, 20, 10],
        pagebreak: {
            mode: ["avoid-all", "css", "legacy"],
            avoid: ['.pdf-section', '.pdf-row', '.pdf-two-column', '.pdf-row-column', '.pdf-row-column>div', '.pdf-two-column>div']
        }
    }

    return new Promise<string>((resolve, reject) => {
        html2pdf().set(options).from(printElement).toPdf().get('pdf')
            .then(function (pdf) {
                appendTimestampFooter(pdf);
                const pdfBytes = pdf.output('arraybuffer');
                const blob = new Blob([pdfBytes], { type: 'application/pdf' });
                const reader = new FileReader();

                reader.onloadend = function () {
                    if (typeof reader.result === 'string') {
                        const base64String = reader.result.split(',')[1];
                        console.log('base64String', base64String);
                        resolve(base64String);
                    } else {
                        reject(new Error('Unexpected result type: ' + typeof reader.result));
                    }
                };

                reader.onerror = function (err) {
                    reject(err);
                };

                reader.readAsDataURL(blob);
            })
            .catch(err => reject(err));
    });

};

/**
 * Parse file object containing uploaded files for an element 
 * into a list of file names, sizes, and total size for pdf population.
 */
const parseFiles = (files) => {

    console.log(files);

    let fileNames = "";
    let fileSizes = "";
    let sizeTotal = 0;

    files.forEach((file) => {
        console.log(file);
        const fileSize = file.file.size / (1024 * 1024); // Convert bytes to MB;
        fileNames += `${file.file.name}\n`;
        fileSizes += `${fileSize.toFixed(2)} MB\n`;
        sizeTotal += fileSize;
    });

    console.log(fileNames, fileSizes, sizeTotal.toFixed(2) + " MB");

    return {
        fileNames,
        fileSizes,
        sizeTotal: sizeTotal.toFixed(2) + " MB"
    };
}

function convertCheckboxesToText(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Find all checkbox groups by name
    const checkboxGroups: Record<string, HTMLInputElement[]> = {};
    const checkboxes = Array.from(doc.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));

    checkboxes.forEach((cb) => {
        const groupName = cb.name || cb.id;
        if (!checkboxGroups[groupName]) checkboxGroups[groupName] = [];
        checkboxGroups[groupName].push(cb);
    });

    // For each group, replace the parent container with selected text
    Object.values(checkboxGroups).forEach((group) => {
        const checkedLabels = group
            .filter((cb) => cb.checked)
            .map((cb) => {
                const label = doc.querySelector(`label[for="${cb.id}"]`);
                return label?.textContent?.trim() || cb.value;
            });

        const parentColumn = group[0].closest(".ontario-form-group");
        if (!parentColumn) return;

        if (checkedLabels.length > 0) {
            // Create a new pdf-two-column div
            const newDiv = doc.createElement("div");
            newDiv.className = "pdf-row pdf-two-column";
            newDiv.setAttribute("style", "page-break-inside: avoid; break-inside: avoid-page; display: table; table-layout: fixed; width: 100%;");

            const titleDiv = doc.createElement("div");
            titleDiv.textContent = parentColumn.querySelector("label")?.textContent || "";

            const valueDiv = doc.createElement("div");
            valueDiv.textContent = checkedLabels.join(", ");

            newDiv.appendChild(titleDiv);
            newDiv.appendChild(valueDiv);

            // Replace the old checkbox group entirely with the new div
            parentColumn.replaceWith(newDiv);
        } else {
            // If nothing is checked, just remove the checkbox group
            parentColumn.remove();
        }
    });

    return doc.documentElement.outerHTML;
}



export const generateHtmlForPdf = async (htmlStr: any, title: string) => {

    const pdfJSX = () => {
        return (

            <html>
                <head>
                    <title>{title}</title>
                    <meta name="author" content="My App" />
                    <meta name="keywords" content="PDF, Report" />
                    <meta name="description" content="PDF Summary Report" />
                    <style>#css#</style>
                </head>
                <body>

                    <div style={{ display: "block", width: "90%", margin: "auto", paddingTop: "24px" }} id="pdf-content">
                        {htmlStr}
                    </div>
                </body>
            </html>
        );
    };

    //const html = ReactDOMServer.renderToString(pdfJSX());
    const html = convertCheckboxesToText(ReactDOMServer.renderToString(pdfJSX()));
    //console.log("HTML STR: ", html);
    //alert(JSON.stringify(htmlStr));
    return JSON.stringify({ html })
};

export const generatePdfFromHtml = async (htmlStr: any, title: string) => {
    const htmlJsonStr = await generateHtmlForPdf(htmlStr, title);
    const htmlForPdf = JSON.parse(htmlJsonStr);
    const url = "data/admin/generatePdf";
    const urlFull = URLUtils.concatUrls(url);
    //const urlFull = "http://localhost:8070/on-form-service/data/admin/generatePdf"
    console.log('urlFull: ', urlFull)
    const response = await fetch(urlFull, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: htmlForPdf.html,
    });

    const pdfData = await response.text();
    console.log('pdfData runtime: ', pdfData)
    //if (pdfData.error) throw new Error(pdfData.error);
    console.log('pdfData base64 length: ', pdfData.length)
    return pdfData;

}
